import { Chip } from '@mi-front/ui';
import { Box, Tooltip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getScheduleStatusChipType,
  getScheduleStatusKind,
  hasScheduleStatusOffTolerance,
  ScheduleStatusCoverage,
} from './get-schedule-status-kind';
import {
  scheduleStatusChipTooltipMessage,
  scheduleStatusChipTooltipOffToleranceMessage,
  scheduleStatusKindMessages,
  scheduleStatusNoAudioMessage,
} from './schedule-status-spot-messages';
import { ScheduleStatusOffToleranceIndicator } from './ScheduleStatusOffToleranceIndicator';

interface ScheduleStatusSpotStatusChipProps {
  status: ScheduleStatusCoverage;
  /** When true, shows the "Sin material" chip next to coverage. */
  missingAudio?: boolean;
  onClick?: () => void;
}

/**
 * Coverage chip. Off-tolerance is a warning icon on Completo/Parcial,
 * not a separate color. Missing audio is a separate chip.
 */
export function ScheduleStatusSpotStatusChip({
  status,
  missingAudio = false,
  onClick,
}: ScheduleStatusSpotStatusChipProps) {
  const { $t } = useIntl();
  const kind = getScheduleStatusKind(status);
  const {
    matchesCount,
    matchesWithScheduleCount,
    matchesWithScheduleOffToleranceCount,
    schedulesCount,
  } = status;
  const offToleranceCount = matchesWithScheduleOffToleranceCount ?? 0;
  const showOffToleranceIcon = hasScheduleStatusOffTolerance(status);

  let tooltip: string;
  if (offToleranceCount > 0) {
    tooltip = $t(scheduleStatusChipTooltipOffToleranceMessage, {
      matchesCount,
      matchesWithScheduleCount,
      schedulesCount,
      offToleranceCount,
    });
  } else {
    tooltip = $t(scheduleStatusChipTooltipMessage, {
      matchesCount,
      matchesWithScheduleCount,
      schedulesCount,
    });
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title={tooltip}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Chip type={getScheduleStatusChipType(kind)} {...(onClick ? { onClick } : {})}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {`${$t(scheduleStatusKindMessages[kind])} ${matchesWithScheduleCount}/${schedulesCount}`}
              {showOffToleranceIcon && <ScheduleStatusOffToleranceIndicator />}
            </Box>
          </Chip>
        </Box>
      </Tooltip>
      {missingAudio && (
        <Chip type="danger">
          <FormattedMessage {...scheduleStatusNoAudioMessage} />
        </Chip>
      )}
    </Box>
  );
}
